import type { ActoDetalle, Folio } from "@/lib/types";
import type { ActoRef, FolioApiClient } from "./client";

// Cliente que llama al backend de Lambdas desplegado en AWS.
// En prod, RPP_BACKEND_URL típicamente apunta al dominio de CloudFront
// (ej. https://rpp.banco.com/api), y CloudFront enruta cada path a su
// Lambda correspondiente.
//
// Espera estos endpoints:
//   GET  {base}/folios/{id}                                         → Folio JSON
//   GET  {base}/folios/{id}/actos/{clave}?control=&consecutivo=&fechaBoleta=
//                                                                   → ActoDetalle JSON
//
// (Los endpoints de PDF y logout viven en su propio handler y se invocan
// directamente desde los componentes Client, no por este cliente.)
export class LambdaFolioClient implements FolioApiClient {
  constructor(private readonly baseUrl: string) {}

  async getFolio(id: string): Promise<Folio | null> {
    const res = await fetch(`${this.base()}/folios/${encodeURIComponent(id)}`, {
      cache: "no-store",
    });
    if (res.status === 404) return null;
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`Backend ${res.status}: ${body.slice(0, 200)}`);
    }
    return (await res.json()) as Folio;
  }

  async getActoDetalle(
    folioId: string,
    ref: ActoRef,
  ): Promise<ActoDetalle | null> {
    const qs = new URLSearchParams({
      control: ref.control,
      consecutivo: ref.consecutivo,
      fechaBoleta: ref.fechaBoleta,
    });
    const res = await fetch(
      `${this.base()}/folios/${encodeURIComponent(folioId)}/actos/${encodeURIComponent(ref.clave)}?${qs}`,
      { cache: "no-store" },
    );
    if (res.status === 404) return null;
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`Backend ${res.status}: ${body.slice(0, 200)}`);
    }
    return (await res.json()) as ActoDetalle;
  }

  private base(): string {
    return this.baseUrl.replace(/\/$/, "");
  }
}
